import React, {useCallback, useEffect, useState} from 'react'
import {Box} from '@mui/material'
import DataTable, {ColumnDef, PaginationState} from 'src/components-new/data/DataTable'
import Filters, {FilterItem, FilterResult} from 'src/components-new/data/Filters'
import InlineMessage from 'src/components-new/feedback/InlineMessage'
import {useToast} from 'src/components-new/feedback/ToastProvider'
import Button from 'src/components-new/primitives/Button'
import AddIcon from '@mui/icons-material/Add'
import {STATUS} from 'src/constants/constants'
import {useAppSelector} from 'src/hooks'
import apiClient from 'src/services/apiClient'
import {buildColumns, DEFAULT_COLUMNS, DigitalEnrollmentRow} from './digitalEnrollmentColumns'
import DigitalEnrollmentDialogNew, {DigitalEnrollmentFormValues} from './DigitalEnrollmentDialogNew'
import DigitalEnrollmentDetailDialogNew from './DigitalEnrollmentDetailDialogNew'
import {DIGITAL_ENROLLMENT_STATE_FILTER_OPTIONS, getActiveColumnLabels, syncFiltersWithColumns} from './enrollmentHubOptions'

interface Option {
  value: string
  label: string
}

// The "Terapia" filter searches on `therapyTypeId`, but the table only ever has a derived
// `therapyName` column (see digitalEnrollmentColumns.tsx) — every other filter id already
// matches its column id 1:1.
const FILTER_COLUMN_ID: Record<string, string> = {therapyTypeId: 'therapyName'}

/**
 * New-style equivalent of src/pages/digitalEnrollment/DigitalEnrollment.tsx. Tab 2
 * ("Adesioni digitali") of the Enrollment Hub. Unlike the legacy page — which reuses the
 * same disabled-fields form as both the creation dialog and the row-detail view — this
 * uses two distinct dialogs (creation vs. read-only detail).
 */
export default function DigitalEnrollmentTab(): JSX.Element {
  const {columnOptions, servicesAvailableForSelection} = useAppSelector(
    state => state.pspConfigReducer.data.enrollmentHubConfigurations?.digitalEnrollmentConfigurations ?? {}
  ) as {columnOptions?: any[], servicesAvailableForSelection?: string[]}
  const toast = useToast()

  const [rows, setRows] = useState<DigitalEnrollmentRow[]>([])
  const [count, setCount] = useState<number>(0)
  const [status, setStatus] = useState<string>(STATUS.PENDING)
  const [statusCount, setStatusCount] = useState<string>(STATUS.PENDING)
  const [activeFilters, setActiveFilters] = useState<FilterResult[]>([])
  const [pagination, setPagination] = useState<PaginationState>({page: 0, rowsPerPage: 10, order: '-createdAt'})
  const [createOpen, setCreateOpen] = useState(false)
  const [detailRow, setDetailRow] = useState<DigitalEnrollmentRow | undefined>()
  const [therapyTypeOptions, setTherapyTypeOptions] = useState<Option[]>([])
  const [serviceTypeOptions, setServiceTypeOptions] = useState<Option[]>([])

  const fetchData = useCallback(() => {
    Promise.all([
      apiClient.getDigitalEnrollment(activeFilters, setStatus, pagination),
      apiClient.getDigitalEnrollmentCount(activeFilters, setStatusCount),
    ])
      .then(([data, countResult]) => {
        if (Array.isArray(data)) setRows(data)
        const n = Number(countResult)
        if (!isNaN(n)) setCount(n)
      })
      .catch(() => setStatus(STATUS.REJECTED))
  }, [activeFilters, pagination])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  useEffect(() => {
    apiClient.getTherapiesTypes(() => undefined).then((data: any) => {
      const list = Array.isArray(data) ? data : []
      setTherapyTypeOptions(list.map((therapy: any) => ({label: therapy.name, value: therapy._id})))
    })
    apiClient.getPackages(() => undefined).then((data: any) => {
      const list = Array.isArray(data) ? data : []
      setServiceTypeOptions(
        list
          .map((service: any) => ({label: service.name, value: service._id}))
          .filter((option: Option) => !servicesAvailableForSelection || servicesAvailableForSelection.includes(option.value))
      )
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Filters are kept in sync with the PSP's columnOptions: a filter only shows up if its
  // column is active, and it adopts that column's label (e.g. a PSP renaming "Cognome
  // Paziente" to "Cognome" renames the filter too).
  const columnLabels = getActiveColumnLabels(columnOptions, DEFAULT_COLUMNS)
  const filterConfig: FilterItem[] = syncFiltersWithColumns([
    {id: 'lastName', label: 'Cognome Paziente', type: 'string'},
    {id: 'firstName', label: 'Nome Paziente', type: 'string'},
    {id: 'therapyTypeId', label: 'Terapia', type: 'select', options: therapyTypeOptions},
    {id: 'state', label: 'Stato', type: 'select', options: DIGITAL_ENROLLMENT_STATE_FILTER_OPTIONS},
  ], columnLabels, FILTER_COLUMN_ID)

  const handleFiltersSubmit = (values: FilterResult[]) => {
    setActiveFilters(values)
    setPagination(prev => ({...prev, page: 0}))
  }

  const handleSaveRequest = async (values: DigitalEnrollmentFormValues) => {
    let finalStatus: string | undefined
    await apiClient.postDigitalEnrollment(values, (s: string) => { finalStatus = s })
    if (finalStatus === STATUS.FULFILLED) {
      toast.success('Richiesta inviata con successo.')
      setCreateOpen(false)
      fetchData()
    } else {
      toast.error("Errore nell'invio della richiesta.")
    }
  }

  const columns: ColumnDef<DigitalEnrollmentRow>[] = buildColumns(columnOptions)

  return (
    <Box>
      <Box sx={{display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2, flexWrap: 'wrap'}}>
        <Button variant="primary" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          {'Nuova'}
        </Button>
        {filterConfig.length > 0 && <Filters config={filterConfig} onSubmit={handleFiltersSubmit} />}
      </Box>

      {(status === STATUS.REJECTED || statusCount === STATUS.REJECTED) && (
        <InlineMessage
          severity="error"
          onClose={() => { setStatus(STATUS.FULFILLED); setStatusCount(STATUS.FULFILLED) }}
          sx={{mb: 2}}
        >
          {'Errore nel caricare le adesioni digitali.'}
        </InlineMessage>
      )}

      <DataTable<DigitalEnrollmentRow>
        columns={columns}
        rows={rows}
        keyField="_id"
        loading={status === STATUS.PENDING || statusCount === STATUS.PENDING}
        emptyMessage="Nessuna adesione trovata"
        onRowClick={setDetailRow}
        totalElements={count}
        pagination={pagination}
        onPaginationChange={setPagination}
        sortBy={pagination.order}
        onSortChange={(order) => setPagination(prev => ({...prev, order, page: 0}))}
      />

      {createOpen && (
        <DigitalEnrollmentDialogNew
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onSave={handleSaveRequest}
          therapyTypeOptions={therapyTypeOptions}
          serviceTypeOptions={serviceTypeOptions}
        />
      )}

      {detailRow && (
        <DigitalEnrollmentDetailDialogNew
          row={detailRow}
          open
          onClose={() => setDetailRow(undefined)}
          therapyTypeOptions={therapyTypeOptions}
          serviceTypeOptions={serviceTypeOptions}
        />
      )}
    </Box>
  )
}
